import { fetchAPIPromise, putAPI } from './utils';

// Older servers still return these keys for clients that used configurable visibility.
const editablePermissions = (data = {}) => {
    const { VIEW_PUBLIC_PROVES, VIEW_PRIVATE_PROVES, VIEW_ADMIN_PROVES, ...permissions } = data || {};
    return permissions;
};

// Defaults that mimic existing hardcoded conditionals in the app
const DEFAULT_CAPABILITIES = {
    ACCESS_WEBAPP: { tecnica: { min: 1 }, junta: { min: 1 }, op: 'OR' },
    ACCESS_SUBVENCIONS: { tecnica: { min: 1 }, junta: { min: 1 }, op: 'OR' },
    MANAGE_USER: { tecnica: { min: 2 }, junta: { min: 2 }, op: 'OR', outrank: true },
    MANAGE_USER_PASSWORDS: { tecnica: { min: 2 }, junta: { min: 2 }, op: 'OR' },
    MANAGE_USER_SESSIONS: { tecnica: { min: 2 }, junta: { min: 2 }, op: 'OR' },
    BULK_RESET_PASSWORDS: { tecnica: { min: 2 }, junta: { min: 2 }, op: 'OR' },
    DELETE_ARCHIVED_MERCHANDISE: { junta: { min: 2 } },
    ACCESS_ALETA_BILLING: { junta: { min: 2 } },
    MANAGE_LEGAL_BOARD: { junta: { min: 2 } },
    MANAGE_PUBLIC_UTILITY: { junta: { min: 2 } },
    VIEW_POSITIONS_STATS: { tecnica: { min: 2 } },
    VIEW_ASSAIGS_STATS: { tecnica: { min: 2 } },
    VIEW_PROVES_STATS: { tecnica: { min: 2 } },
    VIEW_CONNECTIONS_MAP: { tecnica: { min: 2 } },
    MANAGE_REFERENCE_OPTIONS: { tecnica: { min: 2 } },
};

let permissions = { ...DEFAULT_CAPABILITIES };

export const PERMISSION_DESCRIPTIONS = {
    // Gestió d'assajos i proves
    'EDIT_HORA_PROVES': {
        name: 'Canviar hora d\'inici',
        description: 'Permet establir l\'hora d\'inici de les proves d\'un esdeveniment',
        category: 'proves'
    },
    'ADD_PROVA': {
        name: 'Afegir prova',
        description: 'Permet afegir noves proves a un esdeveniment',
        category: 'proves'
    },
    'DELETE_PROVA': {
        name: 'Eliminar prova',
        description: 'Permet suprimir proves existents d\'un esdeveniment',
        category: 'proves'
    },
    'DELETE_PROVA_MESSAGE': {
        name: 'Eliminar missatges de prova',
        description: 'Permet esborrar missatges del xat d\'una prova encara que no siguis l\'autor',
        category: 'proves'
    },
    'SWITCH_EVENT_TARGETS': {
        name: 'Modificar destinataris',
        description: 'Permet canviar a quins col·lectius va dirigit un esdeveniment',
        category: 'proves'
    },
    'EDIT_PROVA': {
        name: 'Gestionar prova',
        description: 'Permet editar i eliminar una prova concreta',
        category: 'proves'
    },
    'MANAGE_DELETED_PROVES': {
        name: 'Paperera de proves',
        description: 'Permet veure i restaurar proves que s\'han eliminat',
        category: 'proves'
    },
    'MANAGE_REFERENCE_OPTIONS': {
        name: 'Gestionar punts de referència',
        description: 'Permet crear i esborrar les opcions guardades del punt de referència i triar la de per defecte de la colla',
        category: 'proves'
    },
    
    // Gestió d'usuaris i rols
    'MANAGE_ROLE_PERMISSIONS': {
        name: 'Gestionar permisos dels rols',
        description: 'Permet configurar quins permisos té cada rol',
        category: 'gestio'
    },
    'DELETE_USER': {
        name: 'Eliminar usuari',
        description: 'Permet esborrar comptes de castellers',
        category: 'gestio'
    },
    'ASSIGN_HEAD_ROLES': {
        name: 'Assignar càrrecs de cap',
        description: 'Permet donar o retirar el rol de cap de tècnica o de junta',
        category: 'gestio'
    },
    'ASSIGN_HEAD_TECNICA': {
        name: 'Assignar cap de tècnica',
        description: 'Permet donar o retirar el rol de cap de tècnica',
        category: 'gestio'
    },
    'ASSIGN_HEAD_JUNTA': {
        name: 'Assignar cap de junta',
        description: 'Permet donar o retirar el rol de cap de junta',
        category: 'gestio'
    },
    'ASSIGN_TECNICA': {
        name: 'Assignar membre de tècnica',
        description: 'Permet assignar el rol de tècnica (nivell 1) a un usuari',
        category: 'gestio'
    },
    'ASSIGN_JUNTA': {
        name: 'Assignar membre de junta',
        description: 'Permet assignar el rol de junta (nivell 1) a un usuari',
        category: 'gestio'
    },
    'BULK_RESET_PASSWORDS': {
        name: 'Restablir contrasenyes massiu',
        description: 'Esborra les contrasenyes de tots els usuaris sense rol (ni tècnica ni junta)',
        category: 'gestio'
    },
    
    // Eines i aplicacions
    'ACCESS_CALENDAR': {
        name: 'Accés al calendari',
        description: 'Permet veure i editar els esdeveniments del calendari',
        category: 'eines'
    },
    'ACCESS_NOTIFICATIONS': {
        name: 'Accés a notificacions',
        description: 'Permet enviar notificacions',
        category: 'eines'
    },
    'ACCESS_FORMS': {
        name: 'Accés a formularis',
        description: 'Permet crear, editar i veure respostes dels formularis',
        category: 'eines'
    },
    'ACCESS_EDITOR': {
        name: 'Accés a l\'editor de plantilles',
        description: 'Permet crear i modificar plantilles de proves',
        category: 'eines'
    },
    'ACCESS_MODELS': {
        name: 'Accés a pinyes model',
        description: 'Permet consultar i modificar les pinyes model',
        category: 'eines'
    },
    'ACCESS_ETIQUETES': {
        name: 'Gestió d\'etiquetes',
        description: 'Permet crear i assignar etiquetes als castellers',
        category: 'eines'
    },
    'ACCESS_FITXAR': {
        name: 'Accés a fitxar',
        description: 'Permet registrar fitxatges en esdeveniments',
        category: 'eines'
    },
    
    // Gestió econòmica
    'ACCESS_LINKS': {
        name: 'Accés a enllaços',
        description: 'Permet crear i administrar enllaços d\'interès',
        category: 'economic'
    },
    'ACCESS_SHOP': {
        name: 'Accés a la botiga',
        description: 'Permet veure i comprar productes de marxandatge',
        category: 'economic'
    },
    'ACCESS_QUOTES': {
        name: 'Accés a Tresoreria',
        description: 'Permet entrar a Tresoreria: consultar quotes, pagaments, patrocinadors i l\'estat de la junta legal',
        category: 'economic'
    },
    'ACCESS_ALETA_BILLING': {
        name: 'Factures d\'Aleta',
        description: 'Permet generar i descarregar les factures del servei Aleta per a la colla',
        category: 'economic'
    },
    'ACCESS_SUBVENCIONS': {
        name: 'Accés a subvencions',
        description: 'Permet veure el calendari de subvencions de cultura',
        category: 'eines'
    },
    'MANAGE_QUOTES': {
        name: 'Gestionar Tresoreria',
        description: 'Permet crear i editar quotes, marcar pagaments i administrar la configuració de pagaments de Tresoreria',
        category: 'economic'
    },
    'MANAGE_LEGAL_BOARD': {
        name: 'Gestionar la junta legal',
        description: 'Permet registrar i modificar la junta legal, la seva tramitació a la Generalitat i l\'actualització a Stripe (Tresoreria > Registre d\'Associacions)',
        category: 'economic'
    },
    'MANAGE_PUBLIC_UTILITY': {
        name: 'Gestionar la utilitat pública',
        description: 'Permet portar la declaració d\'utilitat pública, les obligacions de cada any i el Model 182, i veure els DNI dels donants (Tresoreria > Utilitat pública)',
        category: 'economic'
    },
    'MANAGE_MERCHANDISE': {
        name: 'Gestionar marxandatge',
        description: 'Permet afegir productes i controlar estoc i comandes',
        category: 'economic'
    },
    'DELETE_ARCHIVED_MERCHANDISE': {
        name: 'Eliminar articles arxivats',
        description: 'Permet eliminar definitivament productes arxivats de la botiga',
        category: 'economic'
    },
    
    // Altres
    'VIEW_LAST_CHANGES': {
        name: 'Veure darrers canvis',
        description: 'Mostra el registre de canvis recents a les posicions',
        category: 'altres'
    },
    'VIEW_TECH_STATS': {
        name: 'Veure estadístiques de tècnica',
        description: 'Accés a estadístiques sobre els canvis fets per la tècnica',
        category: 'altres'
    },
    'VIEW_POSITIONS_STATS': {
        name: 'Veure estadístiques de posicions',
        description: 'Accés a estadístiques sobre les posicions dels castellers',
        category: 'altres'
    },
    'VIEW_ASSAIGS_STATS': {
        name: 'Veure estadístiques d\'assaigs',
        description: 'Accés a estadístiques sobre els assaigs',
        category: 'altres'
    },
    'VIEW_PROVES_STATS': {
        name: 'Veure rànquing de proves',
        description: 'Accés al rànquing de castells i plantilles assajats',
        category: 'altres'
    },
    'VIEW_CONNECTIONS_MAP': {
        name: 'Veure mapa de connexions',
        description: 'Accés al mapa de connexions d\'usuaris per ubicació',
        category: 'altres'
    },
    'MANAGE_NOTIFICATION_GROUPS': {
        name: 'Gestionar grups de notificació',
        description: 'Permet crear i editar grups d\'usuaris per a les notificacions',
        category: 'altres'
    },
    'DELETE_FORM_RESPONSE': {
        name: 'Eliminar resposta de formulari',
        description: 'Pot esborrar respostes enviades als formularis',
        category: 'altres'
    },
    'EDIT_TRONCS': {
        name: 'Editar troncs',
        description: 'Permet modificar els troncs i assignar castellers durant els esdeveniments',
        category: 'altres'
    },

    // Altres permisos globals
    'ACCESS_WEBAPP': {
        name: 'Accés a la web',
        description: 'Permet entrar a l\'aplicació web (mínim membre de tècnica o junta)',
        category: 'altres'
    },

    // Gestió d'usuaris
    'MANAGE_USER': {
        name: 'Gestionar usuari',
        description: 'Permet editar un usuari (cap de tècnica o junta, o bé supera el rol objectiu)',
        category: 'gestio'
    },
    'MANAGE_USER_PASSWORDS': {
        name: 'Gestionar contrasenyes',
        description: 'Permet canviar o generar contrasenyes d\'usuaris (caps)',
        category: 'gestio'
    },
    'MANAGE_USER_SESSIONS': {
        name: 'Permetre tancar sessions',
        description: 'Permet tancar la sessió de l\'usuari a tots els dispositius',
        category: 'gestio'
    }
};

export const CATEGORIES = {
    'proves': { name: 'Gestió d\'assajos i proves', icon: '📅' },
    'gestio': { name: 'Gestió d\'usuaris i rols', icon: '👥' },
    'eines': { name: 'Eines i aplicacions', icon: '🛠️' },
    'economic': { name: 'Gestió econòmica', icon: '💰' },
    'altres': { name: 'Altres permisos', icon: '⚙️' }
};

export const ROLE_VALUES = {
    0: { label: 'No', class: 'no' },
    1: { label: 'Membre', class: 'membre' },
    2: { label: 'Cap', class: 'cap' }
};

export const ROLE_NAMES = {
    'tecnica': 'tècnica',
    'junta': 'junta'
};

export const loadPermissions = () => {
    // Always from the server: a cached copy resolves first and the fresh one is then ignored.
    return fetchAPIPromise('/role_permissions', false).then(data => {
        permissions = { ...DEFAULT_CAPABILITIES, ...editablePermissions(data) };
        return permissions;
    });
};

export const can = (userInfo, capability) => {
    const cfg = permissions[capability];
    if (!cfg || !userInfo) return false;

    // A missing role counts as 0, like the server: before the user's roles
    // arrive (userInfo = {}), NaN passed every minimum.
    const check = (level, req) => {
        if (!req) return false;
        const value = parseInt(level, 10) || 0;
        if (req.min !== undefined && value < parseInt(req.min, 10)) return false;
        if (req.eq !== undefined && value !== parseInt(req.eq, 10)) return false;
        if (req.max !== undefined && value > parseInt(req.max, 10)) return false;
        return true;
    };

    const techOK = cfg.tecnica ? check(userInfo.es_tecnica, cfg.tecnica) : false;
    const juntaOK = cfg.junta ? check(userInfo.es_junta, cfg.junta) : false;

    if (cfg.tecnica && cfg.junta) {
        const op = cfg.op || 'OR';
        return op === 'AND' ? (techOK && juntaOK) : (techOK || juntaOK);
    }

    return cfg.tecnica ? techOK : (cfg.junta ? juntaOK : false);
};

// Extended helper for conditionals that depend on both the acting user and a target user's roles
// This centralizes patterns like "cap de tècnica o junta" OR "superar rols de l'usuari objectiu"
export const canForUser = (actorInfo, targetInfo, capability) => {
    if (!actorInfo || !targetInfo) return false;

    // Baseline permission check if configured
    const baseAllowed = can(actorInfo, capability);

    // Outrank logic (editable via backend by setting an `outrank: true` key for that capability)
    const cfg = permissions[capability] || {};
    const wantsOutrank = cfg.outrank === true || capability === 'MANAGE_USER'; // default true for MANAGE_USER to preserve current UX

    const actorTech = parseInt(actorInfo.es_tecnica || 0);
    const actorJunta = parseInt(actorInfo.es_junta || 0);
    const targetTech = parseInt(targetInfo.es_tecnica || 0);
    const targetJunta = parseInt(targetInfo.es_junta || 0);

    const outranks = actorTech >= targetTech && actorJunta >= targetJunta;

    return baseAllowed || (wantsOutrank && outranks);
};

export const savePermissions = (perms, userId) => {
    permissions = editablePermissions(perms);
    return putAPI('/role_permissions', { ...permissions, userId }, () => {});
};

export const getPermissions = () => permissions;

// Derived role helpers (centralize role-based checks used around the app)
export const isTecnica = (userInfo) => parseInt(userInfo?.es_tecnica || 0) > 0;
export const isJunta = (userInfo) => parseInt(userInfo?.es_junta || 0) > 0;
export const isHeadTecnica = (userInfo) => parseInt(userInfo?.es_tecnica || 0) >= 2;
export const isHeadJunta = (userInfo) => parseInt(userInfo?.es_junta || 0) >= 2;
export const isHeadTecnicaOrJunta = (userInfo) => isHeadTecnica(userInfo) || isHeadJunta(userInfo);
